import type { AppStore } from "@/store";
import { clientEnvModule } from "@/modules/client-env";
import { VITE_DISABLE_ONBOARDING } from "@/modules/global-state/globalEnv";
import { localStorageModule } from "@/modules/local-storage";
import type { OnboardingContext } from "./onboarding-types";

/**
 * Production launch boundary for the Proactive trial onboarding experiment.
 *
 * A fixed cutoff keeps older accounts with unfinished legacy onboarding out of
 * the experiment even when they return after launch.
 */
export const PROACTIVE_TRIAL_ONBOARDING_LAUNCH_AT = "2026-07-11T05:23:34.000Z";

const isPostLaunchAccount = (accountCreatedAt: string | null | undefined): boolean => {
  if (!accountCreatedAt) {
    return false;
  }

  const accountCreatedAtMs = Date.parse(accountCreatedAt);
  const launchAtMs = Date.parse(PROACTIVE_TRIAL_ONBOARDING_LAUNCH_AT);

  return Number.isFinite(accountCreatedAtMs) && accountCreatedAtMs >= launchAtMs;
};

/**
 * Restricts normal enrollment to post-launch accounts while preserving the
 * explicit onboarding debug reset on older test accounts.
 */
export const isProactiveTrialOnboardingEligible = ({
  accountCreatedAt,
  hasDebugOverride,
  isOnboardingDisabled = false,
}: {
  accountCreatedAt: string | null | undefined;
  hasDebugOverride: boolean;
  isOnboardingDisabled?: boolean;
}): boolean => !isOnboardingDisabled && (hasDebugOverride || isPostLaunchAccount(accountCreatedAt));

/**
 * Builds a snapshot of all the state sources the onboarding step predicates need.
 *
 * This is the single point of contact between the new OnboardingStore and the
 * legacy state scattered across PlatformOnboardingInfoObservable,
 * PerDeviceOnboardingInfoObservable, and environment modules.
 */
export function buildOnboardingContext(store: AppStore): OnboardingContext {
  const { onboardingInfo, perDeviceOnboardingInfo } = store.platformInfo;
  const { calendarOnboarding, electron } = store;

  /** We only support some features (like voice and meets) on Intel-based MacOS. */
  /** We only require onboarding for them on desktop **/
  const { isMacOs, isDesktop, isIosApp, isIosBrowser, isMacBrowser, isWindowsBrowser } =
    clientEnvModule;
  /* A user sent here from the iOS app already onboarded there, so the web
     wizard would only repeat it. The from-ios-app signal is session-scoped,
     leaving a later organic web visit to onboard normally. */
  const shouldDisableOnboarding = VITE_DISABLE_ONBOARDING || isIosApp();

  const isDesktopEnvironment = isDesktop();
  const onboardingFlowVersion = onboardingInfo.onboardingFlowVersion;
  const onboardingIntent = onboardingInfo.onboardingIntent;
  const intentOnboardingIsCompleted =
    onboardingFlowVersion === "intent_v1" && onboardingInfo.allTasksCompletedAt !== null;
  const isAppleSiliconMacDesktop = isDesktopEnvironment && isMacOs() && !electron.isIntelMacDesktop;
  const proactiveTrialOnboardingDebugOverride =
    perDeviceOnboardingInfo.proactiveTrialOnboardingDebugOverride;
  const isEligibleForProactiveTrialOnboarding = isProactiveTrialOnboardingEligible({
    accountCreatedAt: store.account.myAccount.createdAt,
    hasDebugOverride: proactiveTrialOnboardingDebugOverride,
    isOnboardingDisabled: shouldDisableOnboarding,
  });
  const isProactiveTrialEligibilityAuthoritative =
    store.subscriptions.isProactiveTrialEligibilityAuthoritative;
  const isProactiveTrialEligible =
    isProactiveTrialEligibilityAuthoritative && store.subscriptions.isProactiveTrialEligible;
  const proactiveTrialOnboardingServerProgress =
    store.subscriptions.nonSkippableTrialOnboardingProgress;
  const hasCompletedSharedSignupOnboarding =
    onboardingFlowVersion === "intent_v1"
      ? intentOnboardingIsCompleted
      : isEligibleForProactiveTrialOnboarding &&
        !proactiveTrialOnboardingDebugOverride &&
        proactiveTrialOnboardingServerProgress === "completed";
  const memAgentOnboardingIsRelevant =
    !shouldDisableOnboarding && perDeviceOnboardingInfo.memAgentOnboardingIsRelevant;
  const nonSkippableTrialAssignmentCanSkip =
    store.subscriptions.nonSkippableTrialAssignment?.can_skip;
  const hasProactiveAccess = store.subscriptions.hasProactiveAccess;
  const shouldShowIcpRfi =
    !shouldDisableOnboarding &&
    !hasCompletedSharedSignupOnboarding &&
    onboardingInfo.shouldShowIcpRfi;
  const shouldShowNativeAppDownload =
    !shouldDisableOnboarding &&
    (isIosBrowser() || isMacBrowser() || isWindowsBrowser()) &&
    !localStorageModule.readIosCalendarSetup();
  // The web download blocker follows signup onboarding progress so existing
  // users who revisit this upsell keep the original dismissible behavior.
  const proactiveTrialOnboardingBlocksNativeDownload = (() => {
    if (!isEligibleForProactiveTrialOnboarding) {
      return false;
    }

    if (
      hasProactiveAccess ||
      perDeviceOnboardingInfo.hasCompletedOrSkippedProactiveTrialOnboarding
    ) {
      return false;
    }

    if (proactiveTrialOnboardingDebugOverride) {
      return true;
    }

    if (
      !isProactiveTrialEligibilityAuthoritative ||
      !isProactiveTrialEligible ||
      !perDeviceOnboardingInfo.proactiveTrialOnboardingIsRelevant
    ) {
      return false;
    }

    return proactiveTrialOnboardingServerProgress === "pending";
  })();
  const hasPendingNativeAppDownloadBlockingOnboarding =
    !shouldDisableOnboarding && (shouldShowIcpRfi || proactiveTrialOnboardingBlocksNativeDownload);

  return {
    // Server-driven
    shouldShowIcpRfi,
    hasCompletedSharedSignupOnboarding,
    onboardingFlowVersion,
    onboardingIntent,
    intentOnboardingIsCompleted,
    quickThoughtsCaptureMethod: perDeviceOnboardingInfo.quickThoughtsCaptureMethod,

    // Device-local
    shouldShowCalendarOnboardingSetup:
      !shouldDisableOnboarding &&
      !hasCompletedSharedSignupOnboarding &&
      calendarOnboarding.shouldShow,
    shouldShowMeetingOnboardingSystemPermissions:
      !shouldDisableOnboarding &&
      !hasCompletedSharedSignupOnboarding &&
      perDeviceOnboardingInfo.shouldShowMeetingOnboardingSystemPermissions,
    shouldShowVoiceOnboardingSystemPermissions:
      !shouldDisableOnboarding &&
      !hasCompletedSharedSignupOnboarding &&
      perDeviceOnboardingInfo.shouldShowVoiceOnboardingSystemPermissions,
    hasCompletedOrSkippedNativeAppDownload:
      perDeviceOnboardingInfo.hasCompletedOrSkippedNativeAppDownload,
    hasCompletedMemAgentIntroOnboarding:
      hasCompletedSharedSignupOnboarding || onboardingInfo.hasCompletedTask("mem-agent-intro"),
    proactiveTrialOnboardingIsRelevant:
      !hasCompletedSharedSignupOnboarding &&
      perDeviceOnboardingInfo.proactiveTrialOnboardingIsRelevant,
    hasCompletedOrSkippedProactiveTrialOnboarding:
      hasCompletedSharedSignupOnboarding ||
      perDeviceOnboardingInfo.hasCompletedOrSkippedProactiveTrialOnboarding,
    proactiveTrialOnboardingDebugOverride,
    memAgentOnboardingIsRelevant,
    hasCompletedMemAgentShortcutOnboarding:
      perDeviceOnboardingInfo.hasCompletedMemAgentShortcutOnboarding,
    hasCompletedMemAgentFirstMessageOnboarding:
      perDeviceOnboardingInfo.hasCompletedMemAgentFirstMessageOnboarding,
    existingUserMemAgentOnboardingDeferredUntilAccess:
      perDeviceOnboardingInfo.existingUserMemAgentOnboardingDeferredUntilAccess,
    existingUserMemAgentOnboardingNeedsEvaluation:
      perDeviceOnboardingInfo.existingUserMemAgentOnboardingNeedsEvaluation,
    existingUserMemAgentOnboardingIsRelevant:
      perDeviceOnboardingInfo.existingUserMemAgentOnboardingIsRelevant,
    hasCompletedExistingUserMemAgentIntroOnboarding: onboardingInfo.hasCompletedTask(
      "existing-user-mem-agent-intro"
    ),
    hasCompletedExistingUserProactiveTrialOnboarding: onboardingInfo.hasCompletedTask(
      "existing-user-proactive-trial"
    ),
    hasCompletedExistingUserMemAgentFirstMessageOnboarding: onboardingInfo.hasCompletedTask(
      "existing-user-mem-agent-first-message"
    ),
    // Environment / platform gates
    shouldShowNativeAppDownload,
    hasPendingNativeAppDownloadBlockingOnboarding,
    isDesktop: isDesktopEnvironment,
    isAppleSiliconMacDesktop,
    isWindowsDesktop: isDesktopEnvironment && (clientEnvModule.isWindows?.() ?? false),
    isMemAgentAccessEnabled:
      !shouldDisableOnboarding && store.floatingAgent.isMemAgentAccessEnabled,
    isMemAgentAccessAuthoritative:
      !store.spaceAccounts.fetchMySpaceAccountsState.loading &&
      store.spaceAccounts.fetchMySpaceAccountsState.error === undefined,
    isFloatingAgentOnboardingSurfaceAvailable:
      !shouldDisableOnboarding && store.floatingAgent.isOnboardingSurfaceAvailable,
    hasProactiveAccess,
    isEligibleForProactiveTrialOnboarding,
    isProactiveTrialEligibilityAuthoritative,
    isProactiveTrialEligible,
    nonSkippableTrialAssignmentCanSkip,
    proactiveTrialOnboardingServerProgress,

    // Calendar OAuth
    hasPendingCalendarConnection: calendarOnboarding.pendingCalendarConnection !== null,
  };
}
